import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { profileService } from '../lib/supabase/services/profileService';

const ThemeContext = createContext();

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
  const { user } = useAuth();

  // Check if user has a preference saved in localStorage
  const [darkMode, setDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('darkMode');
    if (savedTheme !== null) {
      return savedTheme === 'true';
    } else {
      // If no saved preference, check system preference
      return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
  });

  const [isLoadingProfile, setIsLoadingProfile] = useState(false);

  // Load theme preference from Supabase on mount
  useEffect(() => {
    if (!user) return;

    const loadProfileSettings = async () => {
      setIsLoadingProfile(true);
      try {
        const result = await profileService.fetch();

        if (result.success && result.data?.settings?.darkMode !== undefined) {
          setDarkMode(result.data.settings.darkMode);
        }
      } catch (error) {
        console.error('[ThemeContext] Error loading profile settings:', error);
      } finally {
        setIsLoadingProfile(false);
      }
    };

    loadProfileSettings();
  }, [user]);

  // Update the HTML element class when darkMode changes
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    // Save preference to localStorage (cache)
    localStorage.setItem('darkMode', darkMode);
  }, [darkMode]);

  // Toggle dark mode and sync to Supabase
  const toggleDarkMode = async () => {
    const newDarkMode = !darkMode;

    // Update UI immediately (saved to localStorage via useEffect)
    setDarkMode(newDarkMode);

    // Sync with Supabase (non-blocking, no revert on failure)
    if (user) {
      try {
        const result = await profileService.updateSettings({ darkMode: newDarkMode });

        if (!result.success) {
          console.warn('[ThemeContext] Failed to sync darkMode to Supabase:', result.error);
          // Don't revert - localStorage already has the new value
        }
      } catch (error) {
        console.error('[ThemeContext] Error syncing darkMode:', error);
        // Don't revert - localStorage already has the new value
      }
    }
  };

  return (
    <ThemeContext.Provider value={{ darkMode, toggleDarkMode, isLoadingProfile }}>
      {children}
    </ThemeContext.Provider>
  );
};